import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Textarea,
  Divider,
  useToast,
} from '@chakra-ui/react';

const CustomFieldGenerationModal = ({
  isOpen,
  onClose,
  campaignId,
  entityType = 'companies',
  onGenerate,
}) => {
  const [prompt, setPrompt] = useState('');
  const toast = useToast();

  const handleGenerate = () => {
    if (!prompt.trim()) {
      toast({
        title: 'Prompt Required',
        description: 'Please enter a prompt for custom field generation',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    const promptValue = prompt.trim();
    setPrompt('');
    onClose();
    onGenerate?.(promptValue);
  };

  const handleClose = () => {
    setPrompt('');
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && prompt.trim()) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const primaryBtn = {
    bg: '#fafafa',
    color: '#0a0a0a',
    borderRadius: 'full',
    fontWeight: '600',
    fontSize: '13px',
    h: '36px',
    _hover: { opacity: 0.85 },
  };

  const outlineBtn = {
    bg: 'transparent',
    borderRadius: 'full',
    fontWeight: '500',
    fontSize: '13px',
    h: '36px',
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'rgba(255,255,255,0.6)',
    _hover: {
      borderColor: 'rgba(255,255,255,0.3)',
      color: 'rgba(255,255,255,0.9)',
      bg: 'rgba(255,255,255,0.04)',
    },
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay bg="rgba(0,0,0,0.85)" />
      <ModalContent
        bg="#111111"
        borderColor="rgba(255,255,255,0.10)"
        borderWidth="1px"
        borderRadius="10px"
        maxW="600px"
      >
        <ModalHeader
          color="#fafafa"
          fontSize="md"
          fontWeight="600"
          letterSpacing="-0.01em"
          borderBottom="1px solid"
          borderColor="rgba(255,255,255,0.08)"
          pb={4}
        >
          Generate Custom Fields
        </ModalHeader>
        <ModalCloseButton
          color="rgba(255,255,255,0.4)"
          borderRadius="full"
          _hover={{ color: 'rgba(255,255,255,0.75)', bg: 'rgba(255,255,255,0.06)' }}
        />

        <ModalBody py={6}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="#a3a3a3" lineHeight="1.7">
              Describe what additional information you&apos;d like to enrich your {entityType} with.
              AI will generate custom fields based on your prompt.
            </Text>

            <Textarea
              placeholder={`Example: Research each company's recent funding rounds and key investors`}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={6}
              bg="#0a0a0a"
              color="#fafafa"
              borderColor="rgba(255,255,255,0.10)"
              borderRadius="8px"
              fontSize="sm"
              _placeholder={{ color: 'rgba(255,255,255,0.2)' }}
              _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
              _focus={{
                borderColor: 'rgba(255,255,255,0.3)',
                boxShadow: 'none',
              }}
            />

            <Divider borderColor="rgba(255,255,255,0.08)" />

            <Text fontSize="xs" color="rgba(255,255,255,0.3)" lineHeight="1.7">
              Preview generation (5 samples) is free. Progress will be shown in the chat interface.
              After generation completes, you can approve or iterate before enriching all {entityType}.
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="rgba(255,255,255,0.08)" pt={4}>
          <HStack spacing={2}>
            <Button {...outlineBtn} onClick={handleClose}>
              Cancel
            </Button>
            <Button {...primaryBtn} onClick={handleGenerate}>
              Generate Preview
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CustomFieldGenerationModal;
